import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

export default class MsalPlugin {
  constructor(options) {
    if (!options || !options.config) {
      throw new Error("MSAL configuration is required");
    }

    const msalConfig = {
      auth: {
        clientId: options.config.clientId,
        authority: `https://login.microsoftonline.com/${options.config.tenant}`,
        redirectUri: options.config.redirectUri,
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri: options.config.redirectUri
      },
      cache: {
        cacheLocation: options.config.cacheLocation || "localStorage",
        storeAuthStateInCookie: false
      },
      system: {
        allowNativeBroker: false,
        loggerOptions: {
          logLevel: 3, // Error
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) return;
            switch (level) {
              case 0: console.error(message); break;
              case 1: console.warn(message); break;
              case 2: console.info(message); break;
              case 3: console.debug(message); break;
            }
          }
        }
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    this.router = options.router;
    this.requireAuthOnInitialize = options.requireAuthOnInitialize !== false;
    this.accounts = [];
    this.currentAccount = null;
    this.interactionInProgress = false;
    this.initializationComplete = false;
  }

  async initialize() {
    try {
      await this.msalInstance.initialize();
      
      // Handle redirect response from login/logout
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        console.log("Redirect response received:", response.account.username);
        this.currentAccount = response.account;
        this.accounts = this.msalInstance.getAllAccounts();
        this.interactionInProgress = false;
      } else {
        // Get existing accounts from cache
        this.accounts = this.msalInstance.getAllAccounts();
        if (this.accounts.length > 0) {
          this.currentAccount = this.accounts[0];
        }
      }
      
      this.initializationComplete = true;
    } catch (error) {
      console.error("MSAL initialization error:", error);
      this.initializationComplete = true;
      throw error;
    }
  }

  isAuthenticated() {
    return this.currentAccount !== null && this.accounts.length > 0;
  }

  async signIn() {
    if (!this.initializationComplete) {
      console.log("Initialization not complete, skipping login...");
      return;
    }
    
    if (this.interactionInProgress) {
      console.log("Login already in progress, skipping...");
      return;
    }

    try {
      this.interactionInProgress = true;
      const loginRequest = {
        scopes: ["openid", "profile", "User.Read"]
      };
      console.log("Starting login redirect...");
      // loginRedirect doesn't return a promise response, it redirects the page
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login error:", error);
      this.interactionInProgress = false;
      throw error;
    }
  }

  async signOut() {
    try {
      const logoutRequest = {
        account: this.currentAccount,
        postLogoutRedirectUri: window.location.origin
      };
      // logoutRedirect doesn't return, it redirects the page
      await this.msalInstance.logoutRedirect(logoutRequest);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  }

  async acquireToken(request) {
    if (!this.isAuthenticated()) {
      throw new Error("User is not authenticated");
    }

    const tokenRequest = {
      account: this.currentAccount,
      scopes: request.scopes
    };

    try {
      // Try to acquire token silently
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
      return response;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interactive method using redirect
        if (this.interactionInProgress) {
          console.log("Interaction already in progress, skipping token redirect");
          throw error;
        }
        try {
          this.interactionInProgress = true;
          console.log("Starting token acquisition redirect...");
          // acquireTokenRedirect doesn't return, it redirects the page
          // The token will be available after redirect via handleRedirectPromise
          await this.msalInstance.acquireTokenRedirect(tokenRequest);
        } catch (interactiveError) {
          console.error("Interactive token acquisition failed:", interactiveError);
          this.interactionInProgress = false;
          throw interactiveError;
        }
      } else {
        console.error("Token acquisition error:", error);
        throw error;
      }
    }
  }

  get user() {
    if (!this.currentAccount) {
      return null;
    }

    // Map MSAL account structure to match ADAL-like structure for compatibility
    return {
      userName: this.currentAccount.username,
      profile: {
        name: this.currentAccount.name,
        email: this.currentAccount.username,
        tid: this.currentAccount.tenantId,
        oid: this.currentAccount.homeAccountId
      }
    };
  }

  getAccount() {
    return this.currentAccount;
  }

  getAllAccounts() {
    return this.accounts;
  }
}
